import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Group, Row } from '@/components/ui/tiles';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { getWebApp, getTgUser, haptic, openInvoiceUrl } from '@/lib/telegram';
import { toast } from 'sonner';
import { APP_VERSION } from '@/lib/version';
import { useOpenSite } from '@/lib/site-link';
import {
  Star,
  Store,
  Smartphone,
  ShieldCheck,
  Snowflake,
  Settings as SettingsIcon,
  LogOut,
  ChevronRight,
  Trophy,
  History as HistoryIcon,
  MessageSquare,
  HelpCircle,
  Activity,
  Fingerprint,
  ExternalLink,
  Check,
  Laptop,
  Loader2,
} from 'lucide-react';
import { SeasonAvatarHat } from '@/components/season/SeasonDetails';

export const Route = createFileRoute('/profile')({
  component: Profile,
});

function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tgUser = getTgUser();
  const { data: backendProfile, isPending: profilePending } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile(),
    placeholderData: keepPreviousData,
  });
  // Баланс приходит конвертированным в display-валюту (по умолчанию RUB) бэком —
  // делим minor units на 100, чтобы получить целые единицы для UI.
  // referral_link строится бэком из username бота, через который юзер залогинился.
  const profile = backendProfile
    ? {
        tgId: backendProfile.tg_user_id,
        username: backendProfile.tg_username,
        firstName: tgUser?.first_name ?? '',
        lastName: tgUser?.last_name ?? null,
        photoUrl: tgUser?.photo_url ?? null,
        language: (backendProfile.locale === 'en' ? 'en' : 'ru') as 'ru' | 'en',
        balanceDisplay: Math.floor(backendProfile.balance_display_minor / 100),
        displayCurrency: backendProfile.display_currency,
        referralCount: backendProfile.referrals_invited,
        referralLink: backendProfile.referral_link ?? '',
      }
    : null;

  const { sub } = useSelectedSubscription();
  const { data: loyalty } = useQuery({
    queryKey: ['loyalty'],
    queryFn: () => api.loyalty(),
    placeholderData: keepPreviousData,
  });
  const { data: info } = useQuery({
    queryKey: ['client-info'],
    queryFn: () => api.info(),
    placeholderData: keepPreviousData,
  });
  const customLinks = [
    ...(info?.custom_buttons?.menu ?? []),
    ...(info?.custom_buttons?.profile ?? []),
  ];
  // «🏪 Свой VPN» (заявка #96): создание живёт в чате бота, диплинк ведёт туда же,
  // куда и кнопка меню. Строка видна только при включённом у бота тумблере.
  const openMirror = () => {
    const tg = getWebApp();
    if (!info?.bot_username) return;
    const url = `https://t.me/${info.bot_username}?start=open_mirror`;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
    tg?.close();
  };
  const openSupport = () => {
    const tg = getWebApp();
    const url =
      info?.support_url ||
      (info?.bot_username ? `https://t.me/${info.bot_username}?start=open_support` : null);
    if (!url) {
      toast.error(t('Support not set up'));
      return;
    }
    if (tg?.openTelegramLink && url.startsWith('https://t.me/')) tg.openTelegramLink(url);
    else if (tg?.openLink) tg.openLink(url);
    else window.open(url, '_blank');
  };

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const displayName =
    (tgUser
      ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ')
      : profile
        ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
        : ''
    ).trim() || t('Tg user fallback');

  const username = tgUser?.username ?? profile?.username ?? null;
  const photoUrl = tgUser?.photo_url ?? profile?.photoUrl ?? null;
  // Первые буквы слов, а не первые символы: `p[0]` у имени «🦊 Лиса» брал половину
  // эмодзи, и в кружке рисовался битый знак. Слова без букв пропускаем.
  const initials =
    displayName
      .split(/\s+/)
      .map((w) => w.match(/\p{L}/u)?.[0] ?? '')
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';

  /** Отправить ссылку другу: выбор чата открывается сразу, без «скопируйте и вставьте».
   *  Копирование остаётся рядом — оно нужно, когда делятся не в Telegram. */
  if (profilePending && !backendProfile) return <ProfileSkeleton />;

  const lastAchievement = loyalty && loyalty.achieved.length > 0
    ? loyalty.achieved[loyalty.achieved.length - 1]
    : null;

  const go = (to: string, state?: Record<string, unknown>) => () => navigate({ to: to as '/', state });

  return (
    <div className="space-y-6 p-4 pb-6">
      {/* Человек по центру, как в профиле Telegram: аватар, имя, ник, подписка одной строкой. */}
      <header className="tl-profile-head flex flex-col items-center pt-4 text-center">
        <div className="relative">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="size-[84px] rounded-full object-cover" />
          ) : (
            <div className="calm-cta grid size-[84px] place-items-center rounded-full text-[28px] font-semibold">{initials}</div>
          )}
          <SeasonAvatarHat />
        </div>
        <h1 className="mt-4 max-w-full truncate text-[26px] font-semibold tracking-[-0.03em]">{displayName}</h1>
        {username && <div className="mt-0.5 max-w-full truncate text-[15px] text-[var(--t-muted)]">@{username}</div>}
        {sub?.status === 'active' && (
          <button
            type="button"
            onClick={go('/subscription')}
            className="relative mt-3 inline-flex h-9 max-w-full items-center gap-2 rounded-full after:absolute after:inset-x-0 after:-inset-y-1 after:content-[''] border border-[var(--t-line)] bg-[var(--t-surface)] px-3.5 text-[13px] font-medium"
          >
            <span className="size-1.5 shrink-0 rounded-full bg-[var(--t-ok)]" />
            <span className="truncate">{sub.planName}</span>
          </button>
        )}
      </header>

      {loyalty && (loyalty.achieved.length > 0 || loyalty.next) && (
        <Group title={t('Loyalty')}>
          <Row
            icon={<Trophy />}
            title={lastAchievement ? lastAchievement.title : t('No status')}
            hint={
              <>
                {t('Renewals count', { count: loyalty.total_renewals })}
                {loyalty.next && loyalty.renewals_to_next !== null && (
                  <>
                    {' · '}
                    {t('Loyalty next', {
                      title: loyalty.next.title,
                      count: loyalty.renewals_to_next,
                      bonus: loyalty.next.bonus_basis_points / 100,
                    })}
                  </>
                )}
              </>
            }
          />
          {/* Вся лестница (идея #5): сколько даёт каждый уровень и сколько до него. */}
          {(loyalty.levels ?? []).map((lvl) => {
            const achieved = lvl.renewal_count <= loyalty.total_renewals;
            const isNext = loyalty.next?.renewal_count === lvl.renewal_count;
            return (
              <div key={lvl.renewal_count} className={cn(!achieved && !isNext && 'opacity-55', isNext && 'bg-[var(--t-fill)]')}>
                <Row
                  icon={<span className="text-[18px]">{lvl.emoji}</span>}
                  title={<span className="line-clamp-2">{lvl.title}</span>}
                  hint={`${t('Loyalty level bonus', { bonus: lvl.bonus_basis_points / 100 })} · ${t('Loyalty level renewals', { count: lvl.renewal_count })}`}
                  right={
                    achieved ? (
                      <Check className="size-[18px] shrink-0 text-[var(--t-ok)]" />
                    ) : isNext && loyalty.renewals_to_next !== null ? (
                      <span className="shrink-0 text-[12px] font-medium text-[var(--t-muted)]">
                        {t('Loyalty level left', { count: loyalty.renewals_to_next })}
                      </span>
                    ) : null
                  }
                />
              </div>
            );
          })}
        </Group>
      )}

      <Group title={t('Profile section account')}>
        <Row icon={<ShieldCheck />} title={t('Profile subscription')} onClick={go('/subscription')} />
        <Row icon={<Smartphone />} title={t('Profile devices')} onClick={go('/devices')} />
        {/* Заморозка живёт шитом на вкладке «Подписка»: просим его сразу открыть. */}
        <Row icon={<Snowflake />} title={t('Profile freeze')} onClick={go('/subscription', { openSheet: 'freeze' })} />
        <Row icon={<HistoryIcon />} title={t('Connections log')} onClick={go('/activity')} />
        {/* Отзывы раньше были вкладкой внизу; партнёр может выключить их целиком. */}
        {api.getAuth().reviewsEnabled && <Row icon={<Star />} title={t('Reviews')} onClick={go('/reviews')} />}
      </Group>

      {/* Кнопки партнёра из редактора «Тексты → Кнопки» (идея #10): те же, что в
          боте. Ссылка уходит в Telegram, экран мини-аппа открывается на месте. */}
      {customLinks.length > 0 && (
        <Group title={t('Links')}>
          {customLinks.map((b, i) => (
            <Row
              key={`${b.text}-${i}`}
              icon={b.path ? <ChevronRight /> : <ExternalLink />}
              title={<span className="line-clamp-2">{b.text}</span>}
              onClick={() => {
                haptic('light');
                if (b.path) navigate({ to: b.path as '/' });
                else if (b.url) openInvoiceUrl(b.url);
              }}
            />
          ))}
        </Group>
      )}

      <Group title={t('Profile section help')}>
        <Row icon={<MessageSquare />} title={t('Support')} onClick={openSupport} />
        <Row icon={<HelpCircle />} title="FAQ" onClick={go('/faq')} />
        {api.getAuth().mirrorsEnabled && info?.bot_username && (
          <Row icon={<Store />} title={t('Own VPN')} hint={<span className="line-clamp-2">{t('Own VPN hint')}</span>} onClick={openMirror} />
        )}
        {backendProfile?.site_login_available && <SiteRow />}
        {/* Раздел отключаем, если партнёр убрал его у своего бота (флаг из auth-ответа). */}
        {api.getAuth().statusPageEnabled && <Row icon={<Activity />} title={t('Network status')} onClick={go('/status')} />}
        <Row icon={<SettingsIcon />} title={t('Settings')} onClick={go('/settings')} />
        <BiometricRow />
      </Group>

      <Group>
        <Row
          icon={<LogOut />}
          danger
          title={t('Profile logout')}
          onClick={() => {
            // Оба хранилища: bot_id живёт в sessionStorage, цвет и прочее в localStorage.
            window.localStorage.clear();
            window.sessionStorage.clear();
            toast.success(t('Local cache cleared'));
          }}
        />
      </Group>

      <p className="pt-2 text-center font-mono text-[12px] text-[var(--t-faint)]">
        {t('Profile version')}: {APP_VERSION} · tg id {tgUser?.id ?? profile?.tgId ?? '—'}
      </p>
    </div>
  );
}

/**
 * Заголовки секций настоящие: их состав от данных не зависит, ждать нечего.
 * Плейсхолдерами кроем человека и строки разделов, той же геометрией, что у экрана.
 */
function ProfileSkeleton() {
  const { t } = useTranslation();
  const sections: [string, number][] = [
    [t('Profile section account'), 5],
    [t('Profile section help'), 5],
  ];
  return (
    <SkeletonPage className="space-y-6 p-4">
      <div className="flex flex-col items-center gap-3 pt-4">
        <Skeleton className="size-[84px] rounded-full" />
        <SkeletonLine size="title" className="w-40" />
        <SkeletonLine size="sm" className="w-24" />
      </div>
      {sections.map(([title, rows]) => (
        <div key={title}>
          <h2 className="mb-2 px-1 text-[13px] font-medium text-[var(--t-muted)]">{title}</h2>
          <SkeletonCard className="min-h-0 divide-y divide-[var(--t-line)] rounded-[var(--r-card)]">
            {Array.from({ length: rows }, (_, i) => (
              <div key={i} className="flex min-h-[62px] items-center gap-3.5 px-4">
                <Skeleton className="size-9 shrink-0 rounded-[var(--r-sm)]" />
                <SkeletonLine className="w-1/3" />
              </div>
            ))}
          </SkeletonCard>
        </div>
      ))}
    </SkeletonPage>
  );
}

/**
 * Тумблер подтверждения покупок биометрией.
 *
 * До этого Face ID/отпечаток запрашивался перед каждой оплатой у всех и отключить его
 * было нельзя. Выключение НЕ снимает подтверждение вовсе: остаётся обычный попап
 * «Оплатить N ₽?», который и защищает от случайного двойного тапа по кнопке оплаты.
 */
function BiometricRow() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(
    () => api.getAuth().biometricConfirmEnabled !== false,
  );
  const [saving, setSaving] = useState(false);

  const toggle = async (next: boolean) => {
    if (saving) return;
    setSaving(true);
    setEnabled(next);
    try {
      await api.setBiometricConfirm(next);
      haptic('success');
    } catch {
      setEnabled(!next); // откат: иначе тумблер врёт про сохранённое состояние
      haptic('error');
      toast.error(t('Setting save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row
      icon={<Fingerprint />}
      title={t('Biometry row')}
      hint={t('Biometry row desc')}
      right={<Switch checked={enabled} disabled={saving} aria-label={t('Biometry row')} onCheckedChange={(v) => void toggle(v)} />}
    />
  );
}


/**
 * «Открыть в браузере» (заявка #142): кабинет на сайте-витрине без пароля. Строка
 * есть, только когда у бота работает витрина (`site_login_available` с сервера).
 */
function SiteRow() {
  const { t } = useTranslation();
  const { open, pending } = useOpenSite();
  return (
    <Row
      icon={<Laptop />}
      title={t('Open in browser')}
      hint={t('Open in browser hint')}
      onClick={open}
      disabled={pending}
      right={
        pending ? (
          <Loader2 className="size-4 animate-spin text-[var(--t-muted)]" />
        ) : (
          <ExternalLink className="size-4 text-[var(--t-faint)]" />
        )
      }
    />
  );
}
